// SMART_NOTIFICATIONS — WEB-4
//
// Web Audio API synth so we ship NO binary SFX file. Sounds are generated
// at runtime from oscillators: a single sine wave, gain ramp, and a 0.3s
// duration keeps the file < 2 KB of source.
//
// Browsers block AudioContext.resume() until the first user gesture; we
// lazily create / unlock the context on the first call to playSound(...).
// Until then, calls are silent (no error).

type Severity = 'info' | 'success' | 'warning' | 'critical'

interface ToneSpec {
  // Frequencies played in sequence (Hz). One entry = single tone.
  freqs: number[]
  // Duration of each tone in seconds.
  step: number
  // Peak gain (0..1). Keep low — speakers vary wildly.
  gain: number
}

const TONES: Record<Severity, ToneSpec> = {
  info: { freqs: [523.25], step: 0.18, gain: 0.08 }, // Do5 short blip
  success: { freqs: [523.25, 659.25, 783.99], step: 0.12, gain: 0.08 }, // Do-Mi-Sol ascending
  warning: { freqs: [659.25, 659.25], step: 0.14, gain: 0.1 }, // double Mi5
  critical: { freqs: [880, 698.46, 880], step: 0.18, gain: 0.12 }, // La-Fa-La alert
}

let ctx: AudioContext | null = null
let unlocked = false
let lastPlay = 0

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  }
  return ctx
}

/** Call from a click/keydown handler to unlock the context. Idempotent. */
export function unlockAudio(): void {
  const c = getCtx()
  if (!c || unlocked) return
  void c.resume().then(() => {
    unlocked = true
  })
}

/**
 * Play a short non-blocking sound matching the severity. Respects:
 *  - max 1 sound per 2 seconds (anti-spam)
 *  - opts.muted / opts.volumePct from user preferences (passed by caller)
 *  - silently no-ops before the first user interaction (browser policy)
 */
export function playSound(
  severity: Severity,
  opts: { muted?: boolean; volumePct?: number } = {},
): void {
  if (opts.muted) return
  const now = Date.now()
  if (now - lastPlay < 2000) return // throttle
  lastPlay = now

  const c = getCtx()
  if (!c || c.state === 'suspended') return

  const spec = TONES[severity] ?? TONES.info
  const volume = Math.max(0, Math.min(1, (opts.volumePct ?? 60) / 100))

  const start = c.currentTime
  spec.freqs.forEach((freq, i) => {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq

    const t0 = start + i * spec.step
    const t1 = t0 + spec.step * 0.9
    // Tiny attack/decay envelope to avoid clicks.
    gain.gain.setValueAtTime(0, t0)
    gain.gain.linearRampToValueAtTime(spec.gain * volume, t0 + 0.01)
    gain.gain.linearRampToValueAtTime(0, t1)

    osc.connect(gain).connect(c.destination)
    osc.start(t0)
    osc.stop(t1 + 0.02)
  })
}
